import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteAllChats, deleteChat, fetchChats, renameChat, useSetChatLastUpdated } from "../api/chatHistory";
import { PaginatedChatsResponse } from "./constants";
import { searchChats } from "../api/searchChats";
import { TaskStatus } from "../Tasks/constants";

export const usePaginatedChats = ({
  userId,
  limit = 20,
}: {
  userId: string;
  limit?: number;
}) => {
  const queryKey = ["chats"];

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchChats({ userId, offset: pageParam, limit }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const { pagination } = lastPage;
      const nextOffset = allPages.length * limit;
      return pagination.currentPage < pagination.totalPages
        ? nextOffset
        : undefined;
    },
    enabled: !!userId,
    staleTime: 3 * 60 * 60 * 1000, // cache for 3 hours
  });
};

export const usePaginatedSearchResults = ({
  limit = 8,
  query = null,
  enabled = true,
}: {
  limit?: number;
  query?: string | null;
  enabled?: boolean;
}) => {
  const queryKey = ["search-results", query];

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => searchChats({ offset: pageParam, limit, query }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const { pagination } = lastPage;
      const nextOffset = allPages.length * limit;
      return pagination.currentPage < pagination.totalPages
        ? nextOffset
        : undefined;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // cache for 5 minutes
  });
};

// forcefully delete cached data
export const useDeleteAllChatsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      deleteAllChats({ user_id: userId }),
    onMutate: async () => {
      const previousChats = queryClient.getQueryData<PaginatedChatsResponse>(["chats"]);

      // optimistically update cache before mutation completes
      queryClient.setQueryData(["chats"], (oldData: PaginatedChatsResponse | undefined) => {
        if (!oldData) return oldData;

        const clearedPages = oldData.pages.map(page => ({
          ...page,
          records: [], // <- Clear all records in each page
        }));

        return { ...oldData, pages: clearedPages };
      });

      return { previousChats };
    },
    onError: (error, variables, context) => {
      if (context?.previousChats) {
        queryClient.setQueryData(["chats"], context.previousChats);
      }
    },
  });
};

export const useDeleteChatMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, userId }: { sessionId: string; userId: string }) =>
      deleteChat({ session_id: sessionId, user_id: userId }),
    onMutate: async (variables) => {
      const { sessionId } = variables;
      const previousChats = queryClient.getQueryData<PaginatedChatsResponse>(["chats"]);

      // optimistically update cache before mutation completes
      queryClient.setQueryData(["chats"], (oldData: PaginatedChatsResponse | undefined) => {
        if (!oldData) return oldData;

        const updatedPages = oldData.pages.map(page => ({
          ...page,
          records: page.records.filter(chat => chat.sessionId !== sessionId),
        }));

        return { ...oldData, pages: updatedPages };
      });
      return { previousChats };
    },

    onError: (error, variables, context) => {
      if (context?.previousChats) {
        queryClient.setQueryData(["chats"], context.previousChats);
      }
    },
  });
};

export const useRenameChatMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ title, sessionId }: { title: string; sessionId: string }) =>
      renameChat({ title, session_id: sessionId }),
    onMutate: async (variables) => {
      const { title, sessionId } = variables;
      const previousChats = queryClient.getQueryData<PaginatedChatsResponse>(["chats"]);

      // optimistically update cache before mutation completes
      queryClient.setQueryData(["chats"], (oldData: PaginatedChatsResponse | undefined) => {
        if (!oldData) return oldData;

        const updatedPages = oldData.pages.map(page => ({
          ...page,
          records: page.records.map(chat =>
            chat.sessionId === sessionId
              ? { ...chat, title }
              : chat,
          ),
        }));

        return { ...oldData, pages: updatedPages };
      });
      return { previousChats };
    },

    onError: (error, variables, context) => {
      if (context?.previousChats) {
        queryClient.setQueryData(["chats"], context.previousChats);
      }
    },
  });
};

export const useRefreshChatLastUpdatedNew = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, title, date }: { sessionId: string; title: string; date: string }) =>
      useSetChatLastUpdated({ sessionId, title, date }),

    onMutate: async () => {
      const previousChats = queryClient.getQueryData<PaginatedChatsResponse>(["chats"]);
      return { previousChats };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chats"],
      });
    },
    onError: (error, variables, context) => {
      if (context?.previousChats) {
        queryClient.setQueryData(["chats"], context.previousChats);
      }
    },
  });
};

// optimistically update the task status shown for that chat session in the sidebar
export const updateTaskStatus = (
  queryClient,
  chatSessionId: string,
  taskScheduleId: string,
  newStatus: TaskStatus,
) => {
  const queryKey = ["chats"];
  queryClient.setQueryData(queryKey, (oldData: PaginatedChatsResponse | undefined) => {
    if (!oldData) return oldData;

    const newPages = oldData.pages.map((page) => {
      const hasTarget = page.records?.some(record => record.sessionId === chatSessionId);

      if (!hasTarget) return page;

      const updatedRecords = page.records.map(record =>
        record.sessionId === chatSessionId
          ? {
              ...record,
              task_schedule:
                record.task_schedule?.id === taskScheduleId
                  ? { ...record.task_schedule, status: newStatus }
                  : record.task_schedule,
            }
          : record,
      );

      return { ...page, records: updatedRecords };
    });

    return { ...oldData, pages: newPages };
  });
};
